import { type ReactNode, createContext, useContext } from "react";

/**
 * Two contexts rather than one, and the split is deliberate.
 *
 * `relocatedIds` answers "does this artifact render here?" and is read by every
 * message in the thread. The slot maps hold built React elements and change
 * whenever the feed does, which on a streaming turn is every token. Bundling
 * them would push that churn through every reader — and because context
 * propagates through a memoized fiber, `React.memo(MessageItem)` cannot stop it:
 * the whole thread re-renders per token, markdown included, which re-parses.
 *
 * Split, the id readers subscribe only to a value that changes when the set of
 * relocated artifacts actually changes, and the slot readers are the two
 * components that render them.
 */

/** Ids the feed must not render at their own inline spot, because they render
 *  somewhere else. Empty by default so consumers outside the message list
 *  (shared threads, the canvas, Storybook) render everything inline. */
const RelocatedIdsContext = createContext<ReadonlySet<string>>(new Set());

type RelocatedSlots = {
  /**
   * The cards to render inside each answer, keyed by that answer's message id.
   * `ResponseArtifactItem` drops the node between the prose and the footer
   * without knowing how a card is rendered — which also keeps it from importing
   * `HeavyArtifactItem`.
   */
  biscuits: ReadonlyMap<number, ReactNode>;
  /** The steps folded out of the feed and above each answer, keyed the same
   *  way. `MessageItem` drops the node in without knowing how a step renders. */
  steps: ReadonlyMap<number, ReactNode>;
};

const EMPTY_SLOTS: RelocatedSlots = { biscuits: new Map(), steps: new Map() };

const RelocatedSlotsContext = createContext<RelocatedSlots>(EMPTY_SLOTS);

export const RelocatedBiscuitProvider = ({
  relocatedIds,
  slots,
  children,
}: {
  relocatedIds: ReadonlySet<string>;
  slots: RelocatedSlots;
  children: ReactNode;
}) => (
  <RelocatedIdsContext.Provider value={relocatedIds}>
    <RelocatedSlotsContext.Provider value={slots}>{children}</RelocatedSlotsContext.Provider>
  </RelocatedIdsContext.Provider>
);

export const useRelocatedBiscuitIds = (): ReadonlySet<string> => useContext(RelocatedIdsContext);

/** The cards belonging to this answer, if any. */
export const useAnchoredBiscuitSlot = (messageId: number): ReactNode =>
  useContext(RelocatedSlotsContext).biscuits.get(messageId);

/** The folded steps belonging to this answer, rendered above it, if any. */
export const useTurnStepsSlot = (messageId: number): ReactNode =>
  useContext(RelocatedSlotsContext).steps.get(messageId);
